import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Coffee, Heart, Cake, Sparkles, Loader2 } from 'lucide-react';
import confetti from 'canvas-confetti';

interface CardData {
  id: string;
  recipient_name: string;
  sender_name: string;
  message: string;
  card_design: string;
  includes_coffee: number;
  coffee_amount: number;
  gift_code: string | null;
  created_at: string;
}

const CardView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [card, setCard] = useState<CardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPlayedConfetti, setHasPlayedConfetti] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchCard = async () => {
      try {
        const response = await fetch(`https://birthday-coffee-api.b-9f2.workers.dev/api/cards/${id}`);

        if (!response.ok) {
          throw new Error('Card not found');
        }

        const data = await response.json();
        setCard(data.card);
        setLoading(false);

        // Trigger confetti after card loads
        if (!hasPlayedConfetti) {
          setTimeout(() => {
            playConfetti();
            setHasPlayedConfetti(true);
          }, 500);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load card');
        setLoading(false);
      }
    };

    fetchCard();
  }, [id]);

  const playConfetti = () => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#FCD34D', '#F59E0B', '#DC2626', '#7C3AED', '#2563EB']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FCD34D', '#F59E0B', '#DC2626', '#7C3AED', '#2563EB']
      });
    }, 250);
  };

  const getGradient = (design: string) => {
    const gradients: Record<string, string> = {
      'coffee-blast': 'from-amber-400 via-orange-500 to-amber-600',
      'cake-party': 'from-pink-400 via-rose-500 to-pink-600',
      'stellar-wishes': 'from-blue-400 via-indigo-500 to-blue-600',
      'heart-warm': 'from-red-400 via-rose-500 to-red-600',
      'golden-celebration': 'from-yellow-400 via-amber-500 to-yellow-600'
    };
    return gradients[design] || gradients['coffee-blast'];
  };

  const getIcon = (design: string) => {
    const icons: Record<string, React.ReactNode> = {
      'coffee-blast': <Coffee className="w-20 h-20" />,
      'cake-party': <Cake className="w-20 h-20" />,
      'stellar-wishes': <Sparkles className="w-20 h-20" />,
      'heart-warm': <Heart className="w-20 h-20" />,
      'golden-celebration': <Sparkles className="w-20 h-20" />
    };
    return icons[design] || icons['coffee-blast'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your birthday card...</p>
        </div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Card Not Found</h2>
          <p className="text-gray-600 mb-6">
            {error || 'This birthday card could not be found.'}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
          >
            Create Your Own Card
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Birthday Card */}
        <div className={`bg-gradient-to-br ${getGradient(card.card_design)} rounded-3xl shadow-2xl p-8 md:p-12 text-white mb-6 transform hover:scale-105 transition-transform duration-300`}>
          <div className="text-center">
            {/* Icon */}
            <div className="mb-6 opacity-90">
              {getIcon(card.card_design)}
            </div>

            {/* Greeting */}
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Happy Birthday, {card.recipient_name}! 🎉
            </h1>

            {/* Message */}
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-lg md:text-xl leading-relaxed">
                {card.message}
              </p>
            </div>

            {/* Coffee Gift */}
            {card.includes_coffee && card.gift_code && (
              <div className="bg-white/30 backdrop-blur-md rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Coffee className="w-8 h-8" />
                  <h3 className="text-2xl font-bold">Virtual Coffee Gift!</h3>
                </div>
                <p className="text-lg mb-4">
                  {card.sender_name} sent you ${card.coffee_amount} for coffee
                </p>
                <div className="bg-white/40 backdrop-blur rounded-xl p-4">
                  <p className="text-sm opacity-90 mb-2">Gift Code:</p>
                  <p className="text-2xl font-mono font-bold tracking-wider">
                    {card.gift_code}
                  </p>
                </div>
                <p className="text-sm mt-3 opacity-90">
                  Use this code at your favorite coffee shop!
                </p>
              </div>
            )}

            {/* Sender */}
            <div className="text-center">
              <p className="text-lg opacity-90">
                With love from,
              </p>
              <p className="text-2xl font-bold mt-1">
                {card.sender_name}
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Want to send your own birthday wishes?
          </h3>
          <p className="text-gray-600 mb-4">
            Create beautiful birthday cards in seconds
          </p>
          <a
            href="/"
            className="inline-block px-8 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
          >
            Create Your Card
          </a>
        </div>
      </div>
    </div>
  );
};

export default CardView;
